// frontend/src/components/admin/PlanesSection.jsx
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useAdminCRUD, useModal, useConfirm } from "../../hooks/useAdmin";
import { Button } from "../shared/Button";
import { Input } from "../shared/Input";
import { Card } from "../shared/Card";
import { Loading } from "../shared/Loading";
import { ErrorMessage } from "../shared/ErrorMessage";
import { Modal } from "../shared/Modal";
import { EmptyState } from "../shared/EmptyState";
import { formatCurrency } from "../../utils/format";
import * as adminApi from "../../services/api";

export function PlanesSection({ primaryColor = "#6366F1" }) {
  const {
    items: planes,
    loading,
    error,
    submitting,
    reload,
    create,
    update,
    remove,
  } = useAdminCRUD(
    {
      fetchAll: adminApi.listarPlanes,
      create: adminApi.crearPlan,
      update: adminApi.editarPlan,
      remove: adminApi.eliminarPlan,
    }
  );

  const formModal = useModal();
  const { confirmAsync } = useConfirm();

  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    nombre: "",
    creditos: "",
    duracion_dias: "",
    precio: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const isEditing = !!formModal.data;

  const planesFiltrados = useMemo(() => {
    if (!searchTerm.trim()) return planes;

    const term = searchTerm.toLowerCase();

    return planes.filter((plan) => {
      const nombre = plan.nombre?.toLowerCase() || "";
      const creditos = plan.creditos?.toString() || "";
      const duracion = plan.duracion_dias?.toString() || "";
      const precio = plan.precio?.toString() || "";

      return (
        nombre.includes(term) ||
        creditos.includes(term) ||
        duracion.includes(term) ||
        precio.includes(term)
      );
    });
  }, [planes, searchTerm]);

  const resultsLabel = useMemo(() => {
    if (!searchTerm.trim()) return `Total: ${planes.length}`;
    if (planesFiltrados.length === 0) return "Sin resultados";
    if (planesFiltrados.length === planes.length) return `Mostrando todos (${planes.length})`;
    return `Mostrando ${planesFiltrados.length} de ${planes.length}`;
  }, [searchTerm, planes.length, planesFiltrados.length]);

  const validateForm = () => {
    const errors = {};

    if (!formData.nombre?.trim()) errors.nombre = "El nombre es requerido";

    const creditos = Number(formData.creditos);
    const duracion = Number(formData.duracion_dias);
    const precio = Number(formData.precio);

    if (!Number.isFinite(creditos) || creditos < 1) errors.creditos = "Créditos debe ser mayor a 0";
    if (!Number.isFinite(duracion) || duracion < 1) errors.duracion_dias = "Duración debe ser mayor a 0";
    if (!Number.isFinite(precio) || precio < 0) errors.precio = "Precio debe ser mayor o igual a 0";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: null }));
  };

  const resetForm = () => {
    setFormData({ nombre: "", creditos: "", duracion_dias: "", precio: "" });
    setFormErrors({});
  };

  const handleCreate = () => {
    formModal.open();
    resetForm();
  };

  const handleEdit = (plan) => {
    formModal.open(plan);
    setFormData({
      nombre: plan.nombre ?? "",
      creditos: plan.creditos ?? "",
      duracion_dias: plan.duracion_dias ?? "",
      precio: plan.precio ?? "",
    });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Revisá los campos marcados", { icon: "⚠️", duration: 3500 });
      return;
    }

    const loadingToast = toast.loading(isEditing ? "Actualizando plan..." : "Creando plan...", {
      style: { borderLeft: `4px solid ${primaryColor}` },
    });

    try {
      const payload = {
        nombre: formData.nombre.trim(),
        creditos: Number(formData.creditos),
        duracion_dias: Number(formData.duracion_dias),
        precio: Number(formData.precio),
      };

      const result = isEditing
        ? await update(formModal.data.id, payload)
        : await create(payload);

      if (result.success) {
        toast.success(isEditing ? "Plan actualizado" : "Plan creado", { icon: "✅", duration: 2500 });
        formModal.close();
      } else {
        toast.error(result.error || "No se pudo guardar el plan", {
          icon: "❌",
          duration: 5000,
          style: { maxWidth: "520px" },
        });
      }
    } catch (err) {
      toast.error("Error inesperado. Intentá nuevamente.", { icon: "❌", duration: 5000 });
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const handleDelete = async (plan) => {
    const confirmed = await confirmAsync(
      `¿Seguro que deseas eliminar el plan "${plan.nombre}"?\n\nSolo se puede eliminar si no tiene suscripciones activas.`
    );

    if (!confirmed) return;

    const loadingToast = toast.loading("Eliminando plan...", {
      style: { borderLeft: `4px solid ${primaryColor}` },
    });

    try {
      const result = await remove(plan.id);

      if (result.success) {
        toast.success("Plan eliminado", { icon: "✅", duration: 2500 });
      } else {
        toast.error(result.error || "No se pudo eliminar el plan", { icon: "❌", duration: 5000 });
      }
    } catch (err) {
      toast.error("Error inesperado. Intentá nuevamente.", { icon: "❌", duration: 5000 });
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  if (loading && planes.length === 0) return <Loading primaryColor={primaryColor} />;
  if (error) return <ErrorMessage message={error} onRetry={reload} />;

  return (
    <div className="space-y-4">
      <Card
        primaryColor={primaryColor}
        title="Planes"
        subtitle="Creá y administrá los planes de suscripción"
        actions={
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-slate-400">{resultsLabel}</span>
            <Button variant="primary" onClick={handleCreate} primaryColor={primaryColor}>
              Nuevo plan
            </Button>
          </div>
        }
      >
        {/* Search */}
        <div className="mb-5">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <input
              type="text"
              placeholder="Buscar por nombre, créditos, duración o precio..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-3 rounded-xl bg-slate-800/40 border border-slate-700 text-white placeholder-slate-500
                         focus:ring-2 focus:border-transparent transition"
              style={{ "--tw-ring-color": primaryColor }}
            />

            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-white"
                aria-label="Limpiar búsqueda"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>

          <div className="mt-2 text-xs text-slate-400">
            {searchTerm.trim() ? (
              planesFiltrados.length === 0 ? (
                <span className="text-red-300">No se encontraron planes</span>
              ) : (
                <span>{resultsLabel}</span>
              )
            ) : (
              <span>{resultsLabel}</span>
            )}
          </div>
        </div>

        {/* Grid */}
        {planesFiltrados.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {planesFiltrados.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                primaryColor={primaryColor}
                onEdit={() => handleEdit(plan)}
                onDelete={() => handleDelete(plan)}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-700 bg-slate-800/20 p-2">
            <EmptyState
              icon={searchTerm ? "🔎" : "📄"}
              title={searchTerm ? "No se encontraron planes" : "No hay planes creados"}
              message={
                searchTerm
                  ? `No hay planes que coincidan con "${searchTerm}"`
                  : "Creá tu primer plan para que los clientes puedan suscribirse."
              }
              primaryColor={primaryColor}
              action={
                !searchTerm && (
                  <Button variant="primary" onClick={handleCreate} primaryColor={primaryColor}>
                    Crear primer plan
                  </Button>
                )
              }
            />
          </div>
        )}
      </Card>

      {/* Modal */}
      <Modal
        isOpen={formModal.isOpen}
        onClose={formModal.close}
        title={isEditing ? "Editar plan" : "Nuevo plan"}
        primaryColor={primaryColor}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nombre del plan"
            name="nombre"
            value={formData.nombre}
            onChange={handleInputChange}
            error={formErrors.nombre}
            required
            placeholder="Ej: Básico, Premium, Full"
            primaryColor={primaryColor}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Créditos"
              name="creditos"
              type="number"
              value={formData.creditos}
              onChange={handleInputChange}
              error={formErrors.creditos}
              required
              min="1"
              placeholder="8"
              primaryColor={primaryColor}
            />

            <Input
              label="Duración (días)"
              name="duracion_dias"
              type="number"
              value={formData.duracion_dias}
              onChange={handleInputChange}
              error={formErrors.duracion_dias}
              required
              min="1"
              placeholder="30"
              primaryColor={primaryColor}
            />
          </div>

          <Input
            label="Precio"
            name="precio"
            type="number"
            step="0.01"
            value={formData.precio}
            onChange={handleInputChange}
            error={formErrors.precio}
            required
            min="0"
            placeholder="15000"
            primaryColor={primaryColor}
          />

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <Button variant="ghost" onClick={formModal.close} type="button" disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={submitting} primaryColor={primaryColor}>
              {submitting ? "Guardando..." : isEditing ? "Actualizar" : "Crear plan"}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function PlanCard({ plan, primaryColor, onEdit, onDelete }) {
  return (
    <Card primaryColor={primaryColor} className="p-5 rounded-2xl hover:shadow-xl transition-all">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-white truncate">{plan.nombre}</h3>
          <p className="text-xs text-slate-400 mt-1">{plan.duracion_dias} días</p>
        </div>

        <div
          className="shrink-0 rounded-xl border px-3 py-2"
          style={{ borderColor: `${primaryColor}35`, backgroundColor: `${primaryColor}12` }}
        >
          <span className="text-sm font-semibold" style={{ color: primaryColor }}>
            {formatCurrency(plan.precio)}
          </span>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        <Row label="Créditos" value={plan.creditos} />
        <Row label="Duración" value={`${plan.duracion_dias} días`} />
      </div>

      <div className="mt-5 flex gap-2">
        <Button variant="ghost" onClick={onEdit} className="flex-1 text-sm py-2">
          Editar
        </Button>
        <Button variant="danger" onClick={onDelete} className="flex-1 text-sm py-2">
          Eliminar
        </Button>
      </div>
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-700 bg-slate-800/25 px-3 py-2">
      <span className="text-xs text-slate-400">{label}</span>
      <span className="text-sm font-medium text-slate-200">{value}</span>
    </div>
  );
}
